import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTexts } from '../context/TextsContext';
import './AuthPage.css';

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithGoogle, loginWithEmail, registerWithEmail } = useAuth();
  const { t } = useTexts();

  // Check for error from callback
  const callbackError = location.state?.error;

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!isLogin && formData.password !== formData.confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      setLoading(false);
      return;
    }

    let result;
    if (isLogin) {
      result = await loginWithEmail(formData.email, formData.password);
    } else {
      result = await registerWithEmail(formData.name, formData.email, formData.password);
    }

    setLoading(false);

    if (result.success) {
      navigate('/dashboard', { replace: true });
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-ambient-glow"></div>
      
      <div className="auth-container">
        <button 
          className="auth-back-button"
          onClick={() => navigate('/')}
          data-testid="auth-back-button"
        >
          ← {t('auth.back', 'Zurück')}
        </button>

        <div className="auth-header">
          <h1 className="auth-title">{t('nav.logo', 'Place of Lights')}</h1>
          <p className="auth-subtitle">
            {isLogin ? t('auth.welcome_back', 'Willkommen zurück') : t('auth.join_community', 'Werde Teil der Gemeinschaft')}
          </p>
        </div>

        {(error || callbackError) && (
          <div className="auth-error" data-testid="auth-error">
            {error || callbackError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          {!isLogin && (
            <div className="auth-input-group">
              <label htmlFor="name">Name</label>
              <input
                id="name"
                name="name"
                type="text"
                placeholder="Dein Name"
                value={formData.name}
                onChange={handleInputChange}
                required={!isLogin}
                data-testid="auth-name-input"
              />
            </div>
          )}

          <div className="auth-input-group">
            <label htmlFor="email">E-Mail</label>
            <input
              id="email"
              name="email"
              type="email"
              placeholder="deine@email.de"
              value={formData.email}
              onChange={handleInputChange}
              required
              data-testid="auth-email-input"
            />
          </div>

          <div className="auth-input-group">
            <label htmlFor="password">Passwort</label>
            <input
              id="password"
              name="password"
              type="password"
              placeholder="••••••••"
              value={formData.password}
              onChange={handleInputChange}
              required
              minLength={6}
              data-testid="auth-password-input"
            />
          </div>

          {!isLogin && (
            <div className="auth-input-group">
              <label htmlFor="confirmPassword">Passwort bestätigen</label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                required={!isLogin}
                minLength={6}
                data-testid="auth-confirm-password-input"
              />
            </div>
          )}

          <button 
            type="submit" 
            className="auth-submit-button"
            disabled={loading}
            data-testid="auth-submit-button"
          >
            {loading ? (
              <span className="auth-button-spinner"></span>
            ) : (
              isLogin ? 'Anmelden' : 'Registrieren'
            )}
          </button>
        </form>

        <div className="auth-divider">
          <span>oder</span>
        </div>

        <button 
          className="auth-google-button"
          onClick={loginWithGoogle}
          data-testid="auth-google-button"
        >
          <svg className="google-icon" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Mit Google anmelden
        </button>

        <p className="auth-toggle">
          {isLogin ? 'Noch kein Konto?' : 'Bereits registriert?'}
          <button 
            type="button"
            onClick={() => setIsLogin(!isLogin)}
            data-testid="auth-toggle-button"
          >
            {isLogin ? 'Jetzt registrieren' : 'Jetzt anmelden'}
          </button>
        </p>
      </div>
    </div>
  );
};

export default AuthPage;
